import React, { Fragment, useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { notBlank } from "../../../util/ext";
import { useMediaQuery } from "@material-ui/core";
import {
  all,
  any,
  append,
  concat,
  equals,
  identity,
  includes,
  join,
  length,
  mergeRight,
  omit,
  pluck,
  uniq,
  without,
} from "ramda";
import { setIds } from "../organizeUtil";
import {
  useModuleAdmin,
  useModuleContent,
  useModuleContentCount,
  useAddToModule,
  useDeleteModuleItems,
} from "../../modules/moduleServices";
import { formatSort } from "../../../util/stringFormat";
import { useModuleContentGroups } from "../../creatorServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown /*, faArchive*/,
} from "@fortawesome/pro-regular-svg-icons";
import { useSelected } from "../OrganizeProvider";
import { useHistory, useLocation } from "react-router-dom";
import { useIsAdmin, useIsCreatorManager } from "../../../auth/authUtils";
import { useSearchQuery } from "../../common/use-searchQuery";

import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import ListItemText from "@material-ui/core/ListItemText";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Skeleton from "@material-ui/lab/Skeleton";

import ContentGroupFilesBrowser from "./ContentGroupFilesBrowser";
import ContentGroupHeader from "./ContentGroupHeader";
import ContentGroupHeaderActions from "./ContentGroupHeaderActions";
import ContentGroupsBreadcrumbs from "./ContentGroupsBreadcrumbs";
import ContentGroupsPanel from "./ContentGroupsPanel";
import ContentSourcesAutocomplete from "../common/ContentSourcesAutocomplete";
import OrganizeButton from "../common/OrganizeButton";
import OrganizeContentGroupsBrowser from "./OrganizeContentGroupsBrowser";
import PublishStatusSwitch from "../../modules/edit/PublishStatusSwitch";
import RealTimeSearchBar from "../../common/RealTimeSearchBar";

const useStyles = makeStyles(theme => ({
  btn: {
    marginRight: 15,
  },

  endIcon: {
    color: theme.palette.primary.brandPrimary,
  },
}));

const Switch = ({ module }) => {
  return module.isLoading ? (
    <Skeleton />
  ) : module.isSuccess ? (
    <PublishStatusSwitch view="header" module={module.data} />
  ) : null;
};

const filterButtons = {
  type: "type",
  sortBy: "sort-by",
};

// availableSortFields: title, type, m.auditUpdated
const initialSearchState = {
  status: "APPROVED",
  limit: 10,
  page: 0,
  orderBy: "m.auditUpdated",
  orderDirection: "desc",
  appliedQuery: "",
  q: "",
  source: {
    projectKey: "",
    projectName: "",
  },
};

const getInitialContentSearchState = initialState =>
  mergeRight(initialSearchState, initialState);

// availableSortFields: child.auditUpdated, child.auditAdded, name
const initialContentGroupsState = {
  appliedQuery: "",
  page: 0,
  limit: 10,
  orderBy: "child.auditUpdated",
  orderDirection: "desc",
  mediaTypes: ["anthology", "collection"],
};

const contentGroupTypes = ["anthology", "collection"];

const OrganizeModuleBrowser = props => {
  const { breadcrumbs, id, SnackAlertHandler, view } = props;
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const isCreatorManager = useIsCreatorManager();
  const isDesktop = useMediaQuery("(min-width:1515px)");
  const { showAlert, SnackAlert } = SnackAlertHandler;

  const { selectedContent, removeAllSelectedContent } = useSelected();

  //-- Manage Filter Button State
  const handleClick = filter => event => {
    event.stopPropagation();
    if (equals(filter, filterButtons.type)) {
      setAnchorElType(event.currentTarget);
    } else {
      setAnchorElSort(event.currentTarget);
    }
  };

  //-- Manage Menu States
  const [anchorElSort, setAnchorElSort] = useState(null);
  const [anchorElType, setAnchorElType] = useState(null);

  const handleTypeClose = event => {
    event.stopPropagation();
    setAnchorElType(null);
  };

  const handleSortClose = event => {
    event.stopPropagation();
    setAnchorElSort(null);
  };

  const handleKeyDown = filter => event => {
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      if (equals(filter, filterButtons.type)) {
        setAnchorElType(event.currentTarget);
      } else {
        setAnchorElSort(event.currentTarget);
      }
    }

    if (event.key === "Tab") {
      return;
    }
  };

  //-- Manage Module
  const moduleResults = useModuleAdmin(id);
  const addToModule = useAddToModule(id);
  const deleteModuleItems = useDeleteModuleItems();
  const [module, setModule] = useState({});
  useEffect(() => {
    if (moduleResults.isSuccess) {
      setModule(moduleResults.data);
    }
  }, [moduleResults.isSuccess, moduleResults.data]);

  //-- Manage Content Groups within Module
  const contentGroupsQuery = useSearchQuery(initialContentGroupsState);
  const [contentGroupsState, setContentGroupsState] = useState({
    ...initialContentGroupsState,
    limit: initialContentGroupsState.limit,
    offset: contentGroupsQuery.state.limit * contentGroupsQuery.state.page,
    sort: `${contentGroupsQuery.state.orderBy} ${contentGroupsQuery.state.orderDirection}`,
    mediaType: join(",", contentGroupsQuery.state.mediaTypes),
  });

  useEffect(() => {
    const { appliedQuery, limit, page } = contentGroupsQuery.state;
    const defaultSort = formatSort({
      orderBy: initialContentGroupsState.orderBy,
      orderDirection: initialContentGroupsState.orderDirection,
    });

    const sort =
      contentGroupsQuery.state.orderBy === null
        ? defaultSort
        : formatSort(contentGroupsQuery.state);

    setContentGroupsState({
      limit,
      offset: limit * page,
      sort,
      mediaType: join(",", contentGroupsQuery.state.mediaTypes),
      q: appliedQuery,
    });
  }, [contentGroupsQuery.state]);

  const [groups, setGroups] = useState([]);
  const contentGroups = useModuleContentGroups(id, contentGroupsState);

  useEffect(() => {
    if (contentGroups.isSuccess) {
      setGroups(prev => {
        const previous = contentGroupsQuery.state.page === 0 ? [] : prev;
        const groups = concat(contentGroups.data.contents, previous);
        return groups;
      });
    }
  }, [contentGroups.isSuccess, contentGroups.data, contentGroupsQuery.state]);

  //-- Manage Module Approved Content
  const initialContentSearchState = getInitialContentSearchState(
    notBlank(location.state) ? location.state.searchState : {},
  );
  const contentQuery = useSearchQuery(initialContentSearchState);
  const [contentQueryState, setContentQueryState] = useState({
    ...initialContentSearchState,
    offset: contentQuery.state.limit * contentQuery.state.page,
    sort: `${contentQuery.state.orderBy} ${contentQuery.state.orderDirection}`,
  });
  useEffect(() => {
    const { appliedQuery, limit, page } = contentQuery.state;
    const sort =
      contentQuery.state.orderBy === null
        ? `${initialContentSearchState.orderBy} ${initialContentSearchState.orderDirection}`
        : `${contentQuery.state.orderBy} ${contentQuery.state.orderDirection}`;

    const queryState = omit(
      ["appliedQuery", "page", "orderBy", "orderDirection"],
      contentQuery.state,
    );
    setContentQueryState({
      ...queryState,
      sort,
      offset: limit * page,
      q: appliedQuery,
      source: queryState.source,
    });
  }, [
    contentQuery.state,
    initialContentSearchState.orderBy,
    initialContentSearchState.orderDirection,
  ]);
  const contentResults = useModuleContent(id, {
    ...contentQueryState,
    source: contentQueryState.source.projectKey,
  });
  const contentCountResults = useModuleContentCount(id, {
    ...contentQueryState,
    source: contentQueryState.source.projectKey,
  });
  const [content, setContent] = useState([]);
  useEffect(() => {
    if (contentResults.isSuccess) {
      setContent(contentResults.data);
    }
  }, [contentResults.isSuccess, contentResults.data]);

  //-- Manage Content Group Filters
  const onSortClick = (orderBy, orderDirection) => event => {
    setAnchorElSort(null);
    contentGroupsQuery.handleUpdateSort({
      property: orderBy,
      order: orderDirection,
    });
  };

  const onTypeClick = type => event => {
    event.stopPropagation();

    const newTypes = event.target.checked
      ? uniq(append(type, contentGroupTypes))
      : without(type, contentGroupTypes);

    contentGroupsQuery.handleUpdateMediaTypes(newTypes);
  };

  const handleEditClick = () => {
    const { pathname, search } = location;
    const fromPath = notBlank(search) ? pathname + search : pathname;
    history.push(`/creator/modules/${id}`, {
      from: fromPath,
    });
  };

  //-- Handle Removing Content from Module
  const handleDeleteItems = useCallback(async () => {
    try {
      const itemsToRemove = pluck("id", selectedContent);

      const ids = setIds({
        mediaType: "module",
        itemsToRemove,
      });

      const body = { id, ids };

      await deleteModuleItems.mutateAsync(body);

      const totalRemoved = length(selectedContent);
      const itemText = equals(totalRemoved, 1) ? "Item" : "Items";

      showAlert("success", [
        `${totalRemoved} ${itemText} Successfully Removed`,
      ]);
    } catch (error) {
      showAlert("error", [error.message]);
    } finally {
      removeAllSelectedContent();
    }
  }, [
    deleteModuleItems,
    id,
    removeAllSelectedContent,
    selectedContent,
    showAlert,
  ]);

  //-- Handle Remove Content Groups from Module
  const removeContentGroup = useCallback(
    async group => {
      try {
        await deleteModuleItems.mutateAsync({ id, ids: [group.id] });

        showAlert("success", [`Content Group Successfully Removed`]);
      } catch (error) {
        showAlert("error", [error.message]);
      }
    },
    [deleteModuleItems, id, showAlert],
  );

  //-- Loading State
  const [contentLoading, setContentLoading] = useState(false);
  useEffect(() => {
    setContentLoading(contentResults.isLoading || deleteModuleItems.isLoading);
  }, [contentResults.isLoading, deleteModuleItems.isLoading]);

  return (
    <Fragment>
      <SnackAlert />

      <OrganizeContentGroupsBrowser
        view={view}
        header={
          <ContentGroupHeader
            loading={moduleResults.isLoading}
            view={view}
            breadcrumbs={
              <ContentGroupsBreadcrumbs
                media={moduleResults}
                breadcrumbs={breadcrumbs}
              />
            }
            statusSwitch={
              all(identity, [isDesktop, isAdmin || isCreatorManager]) && (
                <Switch module={moduleResults} />
              )
            }
            actions={
              any(identity, [isAdmin, isCreatorManager]) ? (
                <ContentGroupHeaderActions
                  group={module}
                  handleEditClick={handleEditClick}
                />
              ) : null
            }
          />
        }
        contentGroupsPanel={
          <ContentGroupsPanel
            loading={contentGroups.isLoading}
            view={view}
            hasMore={contentGroups.isSuccess && contentGroups.data.hasMore}
            parent={module}
            showAlert={showAlert}
            addToParent={addToModule}
            removeFromParent={removeContentGroup}
            totalGroups={
              contentGroups.isSuccess ? contentGroups.data.count : "-"
            }
            contentGroups={groups}
            types={contentGroupsQuery.state.mediaTypes}
            handleContentGroupsPageChange={() =>
              contentGroupsQuery.handleUpdatePage(
                contentGroupsQuery.state.page + 1,
              )
            }
            searchBar={
              <RealTimeSearchBar
                searchBarEnd
                query={contentGroupsQuery.state.appliedQuery}
                onQueryChange={contentGroupsQuery.handleUpdateQuery}
              />
            }
            filterMenu={
              <Fragment>
                <OrganizeButton
                  variant="outlined"
                  color="primary"
                  onClick={handleClick(filterButtons.type)}
                  onKeyDown={handleKeyDown(filterButtons.type)}
                  selected={Boolean(anchorElType)}
                  endIcon={<FontAwesomeIcon icon={faAngleDown} />}
                  classes={{ root: classes.btn, endIcon: classes.endIcon }}
                >
                  Type
                </OrganizeButton>
                <Menu
                  id="filter-groups-by-type-menu"
                  anchorEl={anchorElType}
                  keepMounted
                  open={Boolean(anchorElType)}
                  onClose={handleTypeClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  getContentAnchorEl={null}
                >
                  <MenuItem onClick={event => event.stopPropagation()}>
                    <FormControlLabel
                      label="Anthology"
                      control={
                        <Checkbox
                          name={"anthology"}
                          checked={includes(
                            "anthology",
                            contentGroupsQuery.state.mediaTypes,
                          )}
                          onChange={onTypeClick("anthology")}
                          color="primary"
                        />
                      }
                    />
                  </MenuItem>

                  <MenuItem onClick={event => event.stopPropagation()}>
                    <FormControlLabel
                      label="Collection"
                      control={
                        <Checkbox
                          name={"collection"}
                          checked={includes(
                            "collection",
                            contentGroupsQuery.state.mediaTypes,
                          )}
                          onChange={onTypeClick("collection")}
                          color="primary"
                        />
                      }
                    />
                  </MenuItem>
                </Menu>
                <OrganizeButton
                  variant="outlined"
                  color="primary"
                  onClick={handleClick(filterButtons.sortBy)}
                  onKeyDown={handleKeyDown(filterButtons.sortBy)}
                  selected={Boolean(anchorElSort)}
                  endIcon={<FontAwesomeIcon icon={faAngleDown} />}
                  classes={{ endIcon: classes.endIcon }}
                >
                  Sort By
                </OrganizeButton>
                <Menu
                  id="sort-content-groups-menu"
                  anchorEl={anchorElSort}
                  keepMounted
                  open={Boolean(anchorElSort)}
                  onClose={handleSortClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  getContentAnchorEl={null}
                >
                  <MenuItem
                    selected={
                      contentGroupsQuery.state.orderBy === "name" &&
                      contentGroupsQuery.state.orderDirection === "asc"
                    }
                    onClick={onSortClick("name", "asc")}
                  >
                    <ListItemText>Ascending (A - Z)</ListItemText>
                  </MenuItem>

                  <MenuItem
                    selected={
                      contentGroupsQuery.state.orderBy === "name" &&
                      contentGroupsQuery.state.orderDirection === "desc"
                    }
                    onClick={onSortClick("name", "desc")}
                  >
                    <ListItemText>Descending (Z - A)</ListItemText>
                  </MenuItem>

                  <MenuItem
                    selected={
                      contentGroupsQuery.state.orderBy === "child.auditUpdated"
                    }
                    onClick={onSortClick("child.auditUpdated", "desc")}
                  >
                    <ListItemText>Last Updated</ListItemText>
                  </MenuItem>
                </Menu>
              </Fragment>
            }
          />
        }
        filesPanel={
          <ContentGroupFilesBrowser
            searchState={contentQueryState}
            sourceFilter={contentQuery.state.source}
            searchBar={
              <RealTimeSearchBar
                searchBarEnd
                query={contentQuery.state.appliedQuery}
                onQueryChange={contentQuery.handleUpdateQuery}
              />
            }
            filterMenu={
              <ContentSourcesAutocomplete
                setSource={contentQuery.handleUpdateSource}
                source={contentQuery.state.source}
              />
            }
            loading={contentLoading}
            view={view}
            parent={module}
            content={content}
            handleDeleteItems={handleDeleteItems}
            total={
              contentCountResults.isSuccess
                ? contentCountResults.data.count
                : "-"
            }
            page={contentQuery.state.page}
            handlePageChange={(event, newPage) =>
              contentQuery.handleUpdatePage(newPage)
            }
            handleSortChange={contentQuery.handleUpdateSort}
            order={contentQuery.state.orderDirection}
            orderBy={contentQuery.state.orderBy}
            onRowsPerPageChange={contentQuery.handleUpdateLimit}
            rowsPerPage={contentQuery.state.limit}
            selectedContent={selectedContent}
            SnackAlertHandler={SnackAlertHandler}
          />
        }
      />
    </Fragment>
  );
};

export default OrganizeModuleBrowser;
